import React, { useState } from 'react'
import { func, object, string } from 'prop-types'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import { LinkOut } from '../../../icons'
import { authenticationMessages } from '../../../translations'
import { baseTiltifyUrl, getSubdomainUrl } from '../../../utils/tiltifyUrl'
import { Button } from '../../Button'
import { SignUpForm } from './SignUpForm'

const StyledHeading = styled.div`
  font-size: ${rem(24)};
  font-weight: bold;
  text-align: center;
  margin-bottom: ${rem(24)};
  color: ${({ theme }) => theme.textColor};
`

const StyledLabel = styled.div`
  text-align: center;
  padding-bottom: ${rem(16)};
  color: ${({ theme }) => theme.textColor};
`

const StyledUserType = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledLoginFooter = styled.div`
  margin: ${rem(26)} -${rem(29)} ${rem(4)};
  padding: ${rem(18)} ${rem(16)} 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  display: flex;
  justify-content: center;
`

const StyledUserTypeButton = styled(Button)`
  width: 100%;
  font-weight: bold;
  margin-bottom: ${rem(48)};
  color: ${({ theme }) => (theme.isDark ? theme.darkGrey : theme.darkTextColor)};
`

const StyledButton = styled(Button)`
  text-decoration: underline;
  color: ${({ theme }) => theme.lightBlue};

  span {
    line-height: 1rem;
    display: flex;
  }
`

const StyledCauseButton = styled(Button)(
  ({ theme }) => `
    width: 100%;
    font-weight: bold;
    background-color: ${theme.isDark ? 'transparent' : theme.grey};
    border-color: ${theme.isDark ? theme.darkTextColor : theme.grey};
    color: ${theme.darkTextColor};
    box-sizing: border-box;
    white-space: nowrap;
    margin-bottom: ${rem(34)};

    & > div > span {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: center;
    }
`
)

const StyledIcon = styled.svg(
  ({ theme }) => `
    fill: ${theme.darkTextColor};
  `
)

export const SignUpFlow = ({
  handleRedirect,
  onFlowChange,
  onSignUp,
  openChild,
  errors,
  userTypeVisible,
}) => {
  const { formatMessage } = useIntl()
  const [userType, setUserType] = useState(!userTypeVisible ? null : userTypeVisible)
  return (
    <div id='gtm_signup'>
      <StyledHeading>{formatMessage(authenticationMessages.createAnAccount)}</StyledHeading>
      {!userType && (
        <StyledUserType id='gtm_user-type'>
          <StyledLabel>{formatMessage(authenticationMessages.fundraiserSignUpLabel)}</StyledLabel>
          <StyledUserTypeButton
            size='medium'
            intent='lightBlue'
            onClick={() => {
              setUserType('donor')
            }}
          >
            {formatMessage(authenticationMessages.fundraiserDonor)}
          </StyledUserTypeButton>
          <StyledLabel>{formatMessage(authenticationMessages.causeSignUpLabel)}</StyledLabel>
          <StyledCauseButton
            forwardedAs='a'
            size='medium'
            target='__blank'
            rel='noopener noreferrer'
            href={`${getSubdomainUrl(baseTiltifyUrl(), 'app')}/causes/signup`}
            iconRight={<StyledIcon as={LinkOut} height={20} width={20} />}
          >
            {formatMessage(authenticationMessages.createACauseAccount)}
          </StyledCauseButton>
        </StyledUserType>
      )}
      {userType && (
        <>
          <SignUpForm
            onSignUp={onSignUp}
            errors={errors}
            handleRedirect={handleRedirect}
            openChild={openChild}
          />
          <StyledLoginFooter>
            <StyledButton onClick={onFlowChange}>
              {formatMessage(authenticationMessages.already)}
            </StyledButton>
          </StyledLoginFooter>
        </>
      )}
    </div>
  )
}

SignUpFlow.defaultProps = {
  userTypeVisible: null,
}

SignUpFlow.propTypes = {
  handleRedirect: func,
  onFlowChange: func,
  onSignUp: func,
  errors: object,
  userTypeVisible: string,
  openChild: func,
}
